import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Briefcase, BookOpen, CalendarClock, Upload as UploadIcon, RefreshCw, HelpCircle, X, LogOut, Film, IndianRupee, Settings as SettingsIcon, Trash2, FileSearch } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, API_BASE } from "@/lib/api";
import { logoUrl as buildLogoUrl } from "@/lib/brandKit";
import PlanBadge from "@/components/PlanBadge";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import MobileShell from "@/components/MobileShell";
import AskAiFab from "@/components/AskAiFab";
import { toast } from "sonner";

// Phase 6 Layer B — sidebar visibility is now driven entirely by
// /auth/whoami/permissions. Every item declares the resource it gates
// against; nothing is visible by default. Owner is implicitly included in
// every gate so they see all items. The gate names are the single source
// of truth shared with the backend `RESOURCE_GATES` map — change one,
// everyone gets it.
//
// Gate map (matches services/rbac.py):
//   dashboard / leads / today  -> `studio_member` (any of 5 studio roles)
//   projects                   -> `projects_read` (excludes Sales)
//   delivery                   -> `delivery_read` (excludes Sales)
//   accounts                   -> `payments_write` (Accounts + Owner)
//   catalog                    -> `nav_catalog`   (excludes Sales — note:
//                                  this is distinct from `catalog_read`,
//                                  which is the API read gate Sales DOES
//                                  have for dropdown population)
//   import                     -> `import_use`    (Sales + PM + Owner)
//   settings                   -> `team_manage`   (Owner only)
// Phase 7.0 — per-item icon-tile palette. Each NAV_ITEM key maps to a
// (background tint, icon ink) pair defined as CSS variables in tokens.css.
// Active items override these with a gold gradient (see render block).
const NAV_TILE = {
  dashboard: { bg: "var(--ds-tile-dashboard-bg)", ink: "var(--ds-tile-dashboard-ink)" },
  leads:     { bg: "var(--ds-tile-pipeline-bg)",  ink: "var(--ds-tile-pipeline-ink)" },
  projects:  { bg: "var(--ds-tile-projects-bg)",  ink: "var(--ds-tile-projects-ink)" },
  delivery:  { bg: "var(--ds-tile-delivery-bg)",  ink: "var(--ds-tile-delivery-ink)" },
  accounts:  { bg: "var(--ds-tile-accounts-bg)",  ink: "var(--ds-tile-accounts-ink)" },
  catalog:   { bg: "var(--ds-tile-catalog-bg)",   ink: "var(--ds-tile-catalog-ink)" },
  today:     { bg: "var(--ds-tile-today-bg)",     ink: "var(--ds-tile-today-ink)" },
  import:    { bg: "var(--ds-tile-import-bg)",    ink: "var(--ds-tile-import-ink)" },
  settings:  { bg: "var(--ds-tile-settings-bg)",  ink: "var(--ds-tile-settings-ink)" },
  trash:     { bg: "var(--ds-tile-trash-bg)",     ink: "var(--ds-tile-trash-ink)" },
  "audit-log": { bg: "var(--ds-tile-audit-bg)",  ink: "var(--ds-tile-audit-ink)" },
  help:      { bg: "var(--ds-tile-help-bg)",      ink: "var(--ds-tile-help-ink)" },
};

function navItemAllowed(item, can) {
  return can(item.gate);
}

const NAV_ITEMS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, to: "/dashboard", gate: "studio_member" },
  { key: "leads",     label: "Pipeline",  icon: Users,           to: "/leads",     gate: "studio_member" },
  { key: "projects",  label: "Projects",  icon: Briefcase,       to: "/projects",  gate: "projects_read" },
  { key: "delivery",  label: "Delivery",  icon: Film,            to: "/delivery",  gate: "delivery_read" },
  { key: "accounts",  label: "Accounts",  icon: IndianRupee,     to: "/accounts",  gate: "payments_write" },
  { key: "catalog",   label: "Catalog",   icon: BookOpen,        to: "/catalog",   gate: "nav_catalog" },
  { key: "today",     label: "Follow-ups", icon: CalendarClock,  to: "/today",     badge: true, gate: "studio_member" },
  { key: "import",    label: "Import",    icon: UploadIcon,      to: "/import",    gate: "import_use" },
  { key: "settings",  label: "Settings",  icon: SettingsIcon,    to: "/settings",  gate: "team_manage" },
  { key: "trash",     label: "Trash",     icon: Trash2,          to: "/trash",     gate: "team_manage" },
  { key: "audit-log", label: "Audit Log", icon: FileSearch,      to: "/settings/audit-log", gate: "audit_read_finance" },
  { key: "help",      label: "Help",      icon: HelpCircle,      to: "/help",      gate: "studio_member" },
];

function useFollowupBadge() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        // Phase 6B: sidebar badge = follow-ups due + urgent (Overdue + Critical) ops tasks
        const [fuRes, taskRes] = await Promise.allSettled([
          api.get("/followups/counts"),
          api.get("/tasks/counts"),
        ]);
        const fu = fuRes.status === "fulfilled" ? (fuRes.value.data?.active || 0) : 0;
        const urgent = taskRes.status === "fulfilled" ? (taskRes.value.data?.urgent || 0) : 0;
        if (!cancelled) setActive(fu + urgent);
      } catch {}
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return active;
}

function Sidebar({ studio, user, onLogout, onMobileClose }) {
  const location = useLocation();
  const fuActive = useFollowupBadge();
  const { can } = useAuth();
  const initial = (user?.name || user?.email || "?").slice(0, 1).toUpperCase();
  const studioName = studio?.name || "Nuptial Diaries";
  const roleLabel = (user?.role || "owner").toUpperCase();
  // Module 6C.2 — uploaded brand-kit logos live behind `/api/studios/{id}/logo`
  // (signed by `logo_updated_at`). Legacy `studio.logo_url` was a vestigial
  // field that was never populated by the upload endpoint — collapse to the
  // brand-kit helper as the single source.
  const logoSrc = buildLogoUrl(studio);
  const logoFullUrl = logoSrc ? `${API_BASE.replace(/\/api$/, "")}${logoSrc}` : null;

  return (
    <aside
      data-testid="sidebar"
      className="flex flex-col h-full w-[266px] shrink-0"
      style={{
        backgroundColor: "var(--ds-sidebar-bg)",
        borderRight: "1px solid var(--ds-sidebar-line)",
        boxShadow: "inset -1px 0 0 rgba(255,255,255,.4)",
      }}
    >
      {/* Logo block — gold-gradient monogram tile + studio name in ink */}
      <div className="flex items-start gap-3" style={{ padding: "30px 24px 20px" }}>
        <div className="flex-1 flex items-center gap-3 min-w-0">
          {logoFullUrl ? (
            <img src={logoFullUrl} alt={studioName} className="h-9" data-testid="sidebar-logo" />
          ) : (
            <div
              aria-hidden="true"
              style={{
                width: 40, height: 40,
                borderRadius: 13,
                background: "linear-gradient(150deg,#C8A063,#A87C42)",
                boxShadow: "0 4px 12px rgba(168,124,66,.32)",
                display: "grid", placeItems: "center",
                color: "#FFF7E8",
                fontFamily: "var(--ds-num-font)",
                fontSize: 20, fontWeight: 500,
                flexShrink: 0,
              }}
            >
              {studioName.slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div
              className="truncate"
              style={{
                fontFamily: "var(--ds-num-font)",
                fontSize: 21,
                color: "var(--ds-ink)",
                lineHeight: 1.1,
                letterSpacing: "-0.01em",
              }}
            >
              {studioName}
            </div>
            <div
              style={{
                fontSize: 9,
                letterSpacing: "0.22em",
                color: "var(--ds-accent)",
                fontWeight: 700,
                marginTop: 4,
              }}
            >
              LEAD STUDIO
            </div>
          </div>
        </div>
        {onMobileClose && (
          <button
            data-testid="sidebar-close-mobile"
            onClick={onMobileClose}
            className="lg:hidden"
            style={{ color: "#B9B1A2" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#6E675B")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#B9B1A2")}
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1" style={{ padding: "8px 14px", display: "flex", flexDirection: "column", gap: 3 }} data-testid="sidebar-nav">
        {NAV_ITEMS.map((item) => {
          if (!navItemAllowed(item, can)) return null;
          // Help tab is Studio/Pro/Trial/Grandfathered only — Free plan hides it.
          if (item.key === "help" && (studio?.plan || "free").toLowerCase() === "free") return null;
          const Icon = item.icon;
          const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
          const tile = NAV_TILE[item.key] || { bg: "var(--ds-tile-settings-bg)", ink: "var(--ds-tile-settings-ink)" };
          return (
            <NavLink
              key={item.key}
              to={item.to}
              data-testid={`nav-${item.key}`}
              className="relative flex items-center gap-3"
              style={{
                padding: "8px 11px",
                borderRadius: 13,
                fontFamily: "var(--ds-body-font)",
                fontWeight: 600,
                fontSize: 13.5,
                color: isActive ? "var(--ds-ink)" : "#5F5849",
                background: isActive
                  ? "linear-gradient(100deg,#F4E6CC,#F8EFDD)"
                  : "transparent",
                boxShadow: isActive
                  ? "0 2px 8px rgba(176,141,85,.16), inset 0 0 0 1px rgba(176,141,85,.22)"
                  : "none",
                transition: "background-color .15s ease, color .15s ease",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.background = "var(--ds-sidebar-hover)";
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.background = "transparent";
              }}
            >
              {/* Pastel (or gold when active) icon tile */}
              <span
                aria-hidden="true"
                style={{
                  width: 31, height: 31,
                  borderRadius: 10,
                  display: "grid", placeItems: "center",
                  background: isActive
                    ? "linear-gradient(150deg,#C8A063,#A87C42)"
                    : tile.bg,
                  color: isActive ? "#FFF7E8" : tile.ink,
                  boxShadow: isActive ? "0 3px 7px rgba(168,124,66,.34)" : "none",
                  flexShrink: 0,
                  transition: "background .15s ease",
                }}
              >
                <Icon size={16} strokeWidth={2.2} />
              </span>
              <span className="truncate">{item.label}</span>
              {item.badge && fuActive > 0 && (
                <span
                  data-testid={`nav-badge-${item.key}`}
                  style={{
                    marginLeft: "auto",
                    fontSize: 10,
                    fontWeight: 600,
                    padding: "2px 7px",
                    borderRadius: 9999,
                    background: "var(--ds-neg)",
                    color: "#fff",
                    minWidth: 18,
                    textAlign: "center",
                  }}
                >
                  {fuActive}
                </span>
              )}
              {isActive && !item.badge && (
                <span
                  data-testid={`nav-${item.key}-active-rule`}
                  aria-hidden="true"
                  style={{
                    marginLeft: "auto",
                    width: 6, height: 6,
                    borderRadius: 9999,
                    background: "var(--ds-accent)",
                    flexShrink: 0,
                  }}
                />
              )}
              {isActive && item.badge && (
                <span
                  data-testid={`nav-${item.key}-active-rule`}
                  aria-hidden="true"
                  style={{ display: "none" }}
                />
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Sync now */}
      <div style={{ padding: "14px 16px 8px" }}>
        <button
          data-testid="sync-now-button"
          className="w-full inline-flex items-center justify-center gap-2"
          style={{
            background: "#FFF",
            color: "#8A6A38",
            border: "1px solid #E6DCC9",
            borderRadius: 11,
            padding: "9px 12px",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            transition: "border-color .15s, color .15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "var(--ds-accent)";
            e.currentTarget.style.color = "var(--ds-ink)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#E6DCC9";
            e.currentTarget.style.color = "#8A6A38";
          }}
          onClick={async () => {
            try {
              const { data } = await api.post("/sync/now");
              const c = data.counts;
              toast.success(`Sync: +${c.inserted} · refreshed ${c.refreshed}`);
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Sync failed");
            }
          }}
        >
          <RefreshCw size={13} strokeWidth={2.2} />
          Sync now
        </button>
      </div>

      {/* Module 6C.4 — Plan badge + usage meter */}
      <PlanBadge />

      {/* User card */}
      <div style={{ padding: "8px 16px 22px" }} data-testid="user-block">
        <div
          className="flex items-center gap-3"
          style={{
            background: "#FFF",
            border: "1px solid #ECE3D4",
            borderRadius: 14,
            padding: "9px 11px",
          }}
        >
          <div
            data-testid="user-avatar"
            style={{
              width: 38, height: 38,
              borderRadius: 11,
              background: "linear-gradient(150deg,#C8A063,#A87C42)",
              color: "#FFF7E8",
              display: "grid", placeItems: "center",
              fontFamily: "var(--ds-num-font)",
              fontSize: 16, fontWeight: 500,
              flexShrink: 0,
              boxShadow: "0 3px 7px rgba(168,124,66,.28)",
            }}
          >
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="truncate"
              style={{ color: "var(--ds-ink)", fontWeight: 700, fontSize: 13 }}
              data-testid="user-name"
            >
              {user?.name || user?.email || "—"}
            </div>
            <div
              style={{
                fontSize: 9,
                letterSpacing: "0.22em",
                color: "var(--ds-text-2)",
                fontWeight: 600,
                marginTop: 2,
                textTransform: "uppercase",
              }}
              data-testid="user-role"
            >
              {roleLabel}
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={onLogout}
            aria-label="Sign out"
            title="Sign out"
            className="shrink-0"
            style={{ color: "#B9B1A2", transition: "color .15s" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-ink)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#B9B1A2")}
          >
            <LogOut size={15} strokeWidth={2.2} />
          </button>
        </div>
      </div>
    </aside>
  );
}

export default function Layout({ children }) {
  const { user, studio, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    // Post-logout: send to /login (not "/") because on the app subdomain
    // (app.snapopsai.com) the root path renders the embedded marketing
    // landing from public/index.html, which is now duplicated content
    // against the separate marketing site on snapopsai.com. /login is
    // the clean CRM entry point for unauthenticated visitors.
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--ds-canvas)" }}>
      {/* Module 6D Phase 3 — impersonation banner. Sticky to top across
          the entire layout. Only renders when sessionStorage carries an
          active imp_token; otherwise null. */}
      <ImpersonationBanner />

      {/* Phase 7.1 — Mobile app-shell (top app bar + bottom tab bar +
          overflow menu). Only renders below the `lg:` breakpoint; on
          desktop this component is fully hidden via `lg:hidden` on each
          of its parts. Desktop keeps the sidebar untouched. */}
      <MobileShell onLogout={handleLogout} />

      <div className="flex flex-1 min-h-0">
        {/* Desktop sidebar */}
        <div className="hidden lg:flex">
          <Sidebar studio={studio} user={user} onLogout={handleLogout} />
        </div>

        {/* Main canvas — warm-ivory, generous padding per spec §4.
            Mobile: tighter padding + extra bottom space to clear the
            fixed tab bar (~88px including safe-area). `min-w-0` forces
            the flex child to respect the viewport width even when a
            grandchild (e.g. a long KPI number) has wide min-content. */}
        <main
          className="flex-1 min-w-0"
          style={{
            padding: "20px 16px 108px",
            backgroundColor: "var(--ds-canvas)",
          }}
          data-testid="main-canvas"
        >
          <style>{`
            @media (min-width: 1024px) {
              [data-testid="main-canvas"] { padding: 46px 56px 96px !important; }
            }
          `}</style>
          <div className="max-w-[1500px] mx-auto fade-up">{children}</div>
        </main>
      </div>

      <AskAiFab />
    </div>
  );
}
